import { playUiClick } from '../../../../../assets/preloadSounds';
import './Arrows.css';
import arrowHorizontalImg from './arrow_horizontal.png';
import arrowVerticalImg from './arrow_vertical.png';
import type { CSSProperties, ReactNode } from 'react';

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

type ArrowPosition = {
  left: number;
  top: number;
};

type ArrowsProps = {
  directions?: readonly ArrowDirection[];
  positions?: Partial<Record<ArrowDirection, ArrowPosition>>;
  /** Uniform scale relative to default arrow sizes (vertical 208×84, horizontal 93×197). */
  scale?: number;
  onNavigate?: (direction: ArrowDirection) => void;
  canNavigate?: Partial<Record<ArrowDirection, boolean>>;
  /** When false, only children (e.g. delivery hints) stay visible. */
  showButtons?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Optional overlays (e.g. delivery-hint badges) positioned inside `.map-arrows`. */
  children?: ReactNode;
};

const ARROW_SIZE = {
  up: { width: 208, height: 84 },
  down: { width: 208, height: 84 },
  left: { width: 93, height: 197 },
  right: { width: 93, height: 197 },
} as const;

/**
 * Default positions from map_screen.svg → filter regions:
 * arrow_up    filter3 (436.016 250.911 207.989×83.7744)
 * arrow_down  filter4 (436.014 1505.11 207.989×83.7744)
 * arrow_right filter5 (938.657 837.964 92.9188×196.659)
 * arrow_left  filter6 (48.4248 837.95  92.9188×196.659)
 */
const ARROWS = [
  {
    id: 'up' as const,
    src: arrowVerticalImg,
    className: 'map-arrows__arrow map-arrows__arrow--up',
    left: 436.016,
    top: 250.911,
    label: 'Карта вверх',
  },
  {
    id: 'down' as const,
    src: arrowVerticalImg,
    className: 'map-arrows__arrow map-arrows__arrow--down',
    left: 436.014,
    top: 1505.11,
    label: 'Карта вниз',
  },
  {
    id: 'right' as const,
    src: arrowHorizontalImg,
    className: 'map-arrows__arrow map-arrows__arrow--right',
    left: 938.657,
    top: 837.964,
    label: 'Карта вправо',
  },
  {
    id: 'left' as const,
    src: arrowHorizontalImg,
    className: 'map-arrows__arrow map-arrows__arrow--left',
    left: 48.4248,
    top: 837.95,
    label: 'Карта влево',
  },
] as const;

const ALL_DIRECTIONS: readonly ArrowDirection[] = ['up', 'down', 'left', 'right'];

export function Arrows({
  directions = ALL_DIRECTIONS,
  positions,
  scale = 1,
  onNavigate,
  canNavigate,
  showButtons = true,
  className,
  style,
  children,
}: ArrowsProps) {
  const visible = showButtons
    ? ARROWS.filter((arrow) => directions.includes(arrow.id))
    : [];

  return (
    <div
      className={['map-arrows', className].filter(Boolean).join(' ')}
      style={style}
    >
      {visible.map((arrow) => {
        const enabled = canNavigate?.[arrow.id] ?? true;
        const position = positions?.[arrow.id];
        const size = ARROW_SIZE[arrow.id];

        return (
          <button
            key={arrow.id}
            type="button"
            className={[
              arrow.className,
              enabled ? '' : 'map-arrows__arrow--disabled',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: position?.left ?? arrow.left,
              top: position?.top ?? arrow.top,
              ...(scale !== 1
                ? {
                    width: size.width * scale,
                    height: size.height * scale,
                  }
                : null),
            }}
            aria-label={arrow.label}
            disabled={!enabled}
            onClick={() => {
              if (!enabled) {
                return;
              }
              playUiClick();
              onNavigate?.(arrow.id);
            }}
          >
            <img src={arrow.src} alt="" draggable={false} />
          </button>
        );
      })}
      {children}
    </div>
  );
}
